import { useEffect } from "react";
import Head from "next/head";
import dynamic from "next/dynamic";
import styled from "styled-components";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import _ from "lodash";
import { Section, Container } from "styles/commonStyles";
import Layout from "components/Layout/Layout";
import HeaderActionBar from "components/Header/HeaderActionBar";
import CITY_LIST from "cms/city-data";
import CATEGORY_DATA from "cms/category-data";
import CityDropdown from "components/Groups/CityDropdown";
import Pagination from "components/Pagination/Pagination";
import {
	DEFAULT_NO_OF_ITEMS_PER_PAGE,
	DEFAULT_PAGE_NO,
	PAGE_TYPES,
} from "utils/constants";
import { getGroupMembersData } from "utils/apiClients";
import JobTypesSection from "components/LandingPage/JobTypesSection";
import Mixpanel from "utils/Mixpanel";
import { getUrlPath } from "utils/helpers";
import TagManagerUtils from "utils/TagManager";
import { bpMax } from "theme";
import { JobArea, JobCategory, JobCity } from "types/job";
import { EmployeeData } from "types/employer/EmployeeData";
import EmployerHeader from "components/Employer/EmployerHeader";
import config from "config";
import BrandLines from "components/BrandLines";
import useTrackWebSession from "hooks/useTrackWebSession";
import areaData from "cms/area-data";

// Using 'any' type to resolve conflicting React NodeType issues
const CustomerLogos = dynamic(
	() => import("../../components/LandingPage/CustomerLogos")
) as any;

const FeaturedInLogos = dynamic(
	() => import("../../components/LandingPage/FeaturedInLogos")
) as any;

const EmployerTestiomonials = dynamic(
	() => import("../../components/HomeNew/EmployerTestiomonials"),
	{ ssr: false }
) as any;

const Testimonials = dynamic(
	() => import("../../components/Employer/Testimonials")
) as any;

const CTABanner = dynamic(() => import("../../components/Employer/CTABanner")) as any;

const StatsBanner = dynamic(
	() => import("../../components/LandingPage/StatsBanner")
) as any;

const MemberProfileList = dynamic(
	() => import("../../components/Groups/MemberProfileList")
) as any;

const FAQAndSimilarJobsSection = dynamic(
	() => import("../../components/LandingPage/FAQAndSimilarJobsSection")
) as any;

const CityList = [
	{ id: -1, name: "All", slug: "all" },
	...(CITY_LIST as unknown as JobCity[]),
];

const findCityById = (cityId) => {
	let selectedCity = null;
	(CITY_LIST as unknown as JobCity[]).forEach((element) => {
		if (element.id === cityId) {
			selectedCity = element;
		}
	});
	return selectedCity;
};

export type EmployerPagesPropsType = {
	pageName: string;
	employeesCount: number;
	employeesData: EmployeeData[];
	currentPageNumber: number;
	totalPages: number;
	slug: string;
	cityData?: JobCity;
	categoryData?: JobCategory;
	areaData?: JobArea;
};

const Employer: React.FC<EmployerPagesPropsType> = ({
	pageName,
	employeesCount,
	employeesData,
	currentPageNumber,
	totalPages,
	slug,
	cityData,
	categoryData,
	// eslint-disable-next-line @typescript-eslint/no-shadow
	areaData,
}) => {
	const currentDate = new Date();
	const day = currentDate.getDate();
	const month = currentDate.getMonth() + 1;
	const monthNames = [
		"Jan",
		"Feb",
		"Mar",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"November",
		"December",
	];
	const year = currentDate.getFullYear();
	const date = `${day}-${month}-${year}`;
	let metaTitle = "";
	let metaDescription = "";
	let heading1 = "";
	let heading2 = "";
	let subHeading = "";
	const displayPageNumber =
		currentPageNumber && currentPageNumber !== 1 ? `| Page ${currentPageNumber}` : "";

	if (slug === "") {
		metaTitle = `Employer Jobs, ${employeesCount} Employer Job Vacancies In ${
			monthNames[month - 1]
		} ${year}`;
		metaDescription = `Post your Job requirements and connect with ${employeesCount} registered candidates on apna. ✓ Hire Employees in 48 Hours. ✓ Verified Profiles. ${displayPageNumber}`;
		heading1 = `Post Jobs`;
		subHeading = "Hire Employees";
	} else if (cityData?.name && categoryData?.type && areaData?.name) {
		metaTitle = `Hire ${categoryData?.type} Employees in ${areaData.name}|${cityData?.name} City ${displayPageNumber}`;
		metaDescription = `${date} - Post your ${categoryData?.type} Job vacancy in ${areaData.name}|${cityData?.name} city and connect with ${employeesCount} registered candidates for Free on apna. ✓ Hire Employees in 48 Hours. ✓ Verified Profiles. ${displayPageNumber}`;
		heading1 = `Hire ${categoryData?.type}`;
		heading2 = "Employees in";
		subHeading = `${areaData.name} ${cityData?.name}`;
	} else if (cityData?.name && areaData?.name) {
		metaTitle = `Jobs In ${cityData?.name}, Job Vacancies In ${cityData?.name} ${
			monthNames[month - 1]
		} ${year}`;
		metaDescription = `Post your job on Apna and find suitable employees from ${employeesCount} skilled candidates in ${areaData.name}, ${cityData.name} ✓ Relevant candidiates ✓ Best-in-class AI enabled platform`;
		heading1 = `Hire`;
		heading2 = "Employees in";
		subHeading = `${areaData.name}, ${cityData?.name}`;
	} else if (cityData?.name && categoryData?.type) {
		metaTitle = `Start hiring qualified candidates for ${categoryData?.type} in ${cityData?.name}`;
		metaDescription = `Post your job on Apna and find suitable employees from ${employeesCount} skilled candidates for ${categoryData?.type} in ${cityData.name} ✓ Relevant candidiates ✓ Best-in-class AI enabled platform`;
		heading1 = `Hire ${categoryData?.type}`;
		heading2 = "Employees in";
		subHeading = `${cityData?.name}`;
	} else if (cityData?.name) {
		metaTitle = `#1 Hiring Site in ${cityData?.name}| Post your job now | `;
		metaDescription = `Post your job on Apna and find suitable employees from ${employeesCount} skilled candidates in ${cityData.name} ✓ Relevant candidiates ✓ Best-in-class AI enabled platform`;
		heading1 = "Hire Employees";
		subHeading = `in ${cityData?.name}`;
	} else if (categoryData?.type) {
		metaTitle = `Start hiring qualified candidats for ${categoryData?.type}`;
		metaDescription = `Post your job on Apna and find suitable employees from ${employeesCount} skilled candidates for ${categoryData?.type} ✓ Relevant candidiates ✓ Best-in-class AI enabled platform`;
		heading1 = `Hire ${categoryData?.type}`;
		subHeading = "Employees";
	} else {
		metaTitle = `Employer Jobs, ${employeesCount} Employer Job Vacancies In ${
			monthNames[month - 1]
		} ${year}`;
	}
	useTrackWebSession(pageName);
	const router = useRouter();
	useEffect(() => {
		// eslint-disable-next-line camelcase
		Mixpanel.track("Page View", {
			"Profile URL": typeof window !== "undefined" && window.location.href,
			Source: pageName,
			page: getUrlPath(),
			city: cityData?.slug,
			category: categoryData?.type,
			pageType: "Employer Page",
		});

		TagManagerUtils.addDataLayer({
			employer: {
				"Profile URL": typeof window !== "undefined" && window.location.href,
				city: cityData?.name,
			},
			Source: pageName,
			city: cityData?.name,
		});
	}, []);

	const handleCityDropdownChange = (e) => {
		e.preventDefault();
		const { value } = e.currentTarget;
		if (value === -1 && categoryData?.id) {
			router.push(`/employer/hire-${categoryData?.slug}-employees`);
		} else if (value !== -1 && categoryData?.id) {
			const selectedCity = findCityById(parseInt(value, 10));
			router.push(
				`/employer/hire-${
					categoryData?.slug
				}-in-${selectedCity?.slug.toLocaleLowerCase()}`
			);
		} else if (value === -1) {
			router.push("/employer");
		} else {
			const selectedCity = findCityById(parseInt(value, 10));
			router.push(
				`/employer/job-posting-site-in-${selectedCity?.slug.toLocaleLowerCase()}`
			);
		}
		e.currentTarget.blur();
	};

	return (
		<Layout
			title={metaTitle}
			description={metaDescription}
			metaTitle={metaTitle}
			source={pageName}
			headerBgColor="#4d3951"
			hideIntercom
			hideCustomerLogosSection
			hideStatsBanner
			headerColor="#4d3951"
			hideHeading
			hideMainHeader
			hideBrandLines
			cityDropdown={
				<CityDropdown
					onChange={handleCityDropdownChange}
					value={cityData?.id || 0}
					bgColor="#2f2331"
					margin="0px 15px 0px 0px"
					cityList={CityList}
				/>
			}
			rightActionBar={
				<HeaderActionBar
					border="1px solid #ffffff"
					isEmployerPage
					source={pageName}
					customUtmMedium={PAGE_TYPES.short}
				/>
			}
			customUtmMedium={PAGE_TYPES.short}
			slug={`/employer/${slug}${
				currentPageNumber > 1 ? `?page=${currentPageNumber}` : ""
			}`}
		>
			<Head>
				{currentPageNumber > 1 && (
					<link
						rel="prev"
						href={`${config.websiteUrl.slice(0, -1)}/employer${
							slug ? `/${slug}` : ""
						}${
							currentPageNumber - 1 > 1
								? `?page=${currentPageNumber - 1}`
								: ""
						}`}
					/>
				)}
				{currentPageNumber < totalPages && currentPageNumber < 5 && (
					<link
						rel="next"
						href={`${config.websiteUrl.slice(0, -1)}/employer${
							slug ? `/${slug}` : ""
						}?page=${currentPageNumber + 1}`}
					/>
				)}
				<link rel="alternate" href="android-app://com.apnatime" />
			</Head>
			<BrandLines />
			<EmployerHeader
				heading1={heading1}
				heading2={heading2}
				subHeading={subHeading}
			/>
			<Container>
				<MembersListContainer>
					<MembersListWrapper>
						{employeesData?.length > 0 &&
							employeesData.map((item) => (
								<MemberProfileList
									key={item.slug}
									member={item}
									source={pageName}
									pageType={pageName}
									className="employer-page-members-list"
									isEmployerPage
									customUtmMedium={PAGE_TYPES.short}
								/>
							))}
					</MembersListWrapper>
				</MembersListContainer>

				{totalPages > 1 && (
					<Pagination
						totalPages={totalPages > 6 ? 5 : totalPages}
						currentPage={currentPageNumber}
						baseUrl={`/employer/${slug}`}
						routerBased
					/>
				)}
			</Container>
			<CustomerLogos bgColor="#f8f8f8" />
			{slug === "" ? (
				<Container>
					<Testimonials />
				</Container>
			) : (
				<Section>
					<EmployerTestiomonials showHeading />
				</Section>
			)}

			<FAQAndSimilarJobsSection
				source="Employer Pages"
				sectionBgColor="#f5f5f5"
				showFaqOnly
			/>
			<FeaturedInLogos />
			<StatsBanner />
			<CTABanner text="Get your first hire within" bolderText="48 hours" />
			<JobTypesSection
				noMargin
				city={cityData?.name}
				cityId={cityData?.id}
				area={undefined}
				source={pageName}
			/>
		</Layout>
	);
};

// styles
const MembersListContainer = styled.div`
	margin-top: 50px;
`;
const MembersListWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	& .employer-page-members-list {
		width: 50%;
		padding: 0 10px 0 10px;
		${bpMax.md} {
			width: 100%;
		}
	}
`;

export const getServerSideProps: GetServerSideProps<EmployerPagesPropsType> = async ({
	params,
	query,
	res,
}) => {
	res.setHeader("Cache-Control", "public, s-maxage=43200, stale-while-revalidate");
	try {
		const { employer } = params as { employer: string };

		const currentPageNumber = query?.page
			? parseInt(query?.page as string, 10)
			: DEFAULT_PAGE_NO;
		if (!employer) {
			const { employeesData, totalPages } = await getEmployeesDetails(
				null,
				null,
				null,
				currentPageNumber
			);

			return {
				props: {
					pageName: "Employer Page",
					employeesCount: employeesData.count,
					employeesData: employeesData.users,
					currentPageNumber,
					totalPages,
					slug: "",
				},
			};
		}
		if (/^job-posting-site-in-\w+$/.test(employer[0])) {
			// for /employer/free-job-posting-site-in-{citySlug}
			try {
				const [, , , , citySlug] = employer[0].split("-");
				const { parsedCity } = parseParams(null, citySlug, null);
				const { employeesData, totalPages } = await getEmployeesDetails(
					null,
					parsedCity,
					null,
					currentPageNumber
				);
				return {
					props: {
						pageName: "City Employer Pages",
						employeesCount: employeesData.count,
						employeesData: employeesData.users,
						currentPageNumber,
						totalPages,
						slug: employer[0],
						cityData: parsedCity,
					},
				};
			} catch (error) {
				// noop
			}
		} else if (/^hire-\w+-employees$/.test(employer[0])) {
			// for /employer/hire-{categoryType}-employees
			const [, categorySlug] = employer[0].split("-");
			const { parsedCategory } = parseParams(categorySlug, null, null);
			const { employeesData, totalPages } = await getEmployeesDetails(
				parsedCategory,
				null,
				null,
				currentPageNumber
			);
			return {
				props: {
					pageName: "Category Employer Pages",
					employeesCount: employeesData.count,
					employeesData: employeesData.users,
					currentPageNumber,
					totalPages,
					slug: employer[0],
					cityData: null,
					areaData: null,
					categoryData: parsedCategory,
				},
			};
		} else if (/^hire-\w+-in-\w+$/.test(employer[0])) {
			// for /employer/hire-{categoryType}-in-{citySlug}
			const [, categorySlug, , citySlug] = employer[0].split("-");

			const { parsedArea, parsedCity, parsedCategory } = parseParams(
				categorySlug,
				citySlug,
				null
			);
			const { employeesData, totalPages } = await getEmployeesDetails(
				parsedCategory,
				parsedCity,
				parsedArea,
				currentPageNumber
			);

			return {
				props: {
					pageName: "City Category Employer Pages",
					employeesCount: employeesData.count,
					employeesData: employeesData.users,
					currentPageNumber,
					totalPages,
					slug: employer[0],
					cityData: parsedCity,
					areaData: null,
					categoryData: parsedCategory,
				},
			};
		} else if (/hire-for-\w+-in-\w+-\w+$/.test(employer[0])) {
			// for employer/hire-for-{categorySlug}-in-{areaSlug}-{citySlug}
			const [, , categorySlug, , areaSlug, citySlug] = employer[0].split("-");

			const { parsedArea, parsedCity, parsedCategory } = parseParams(
				categorySlug,
				citySlug,
				areaSlug
			);

			const { employeesData, totalPages } = await getEmployeesDetails(
				parsedCategory,
				parsedCity,
				parsedArea,
				currentPageNumber
			);

			return {
				props: {
					pageName: "City Category Employer Pages",
					employeesCount: employeesData.count,
					employeesData: employeesData.users,
					currentPageNumber,
					totalPages,
					slug: employer[0],
					cityData: parsedCity,
					areaData: parsedArea,
					categoryData: parsedCategory,
				},
			};
		} else if (/hire-employees-in-\w+-\w+$/.test(employer[0])) {
			try {
				const [, , , areaSlug, citySlug] = employer[0].split("-");
				const { parsedCity, parsedArea } = parseParams(null, citySlug, areaSlug);

				const { employeesData, totalPages } = await getEmployeesDetails(
					null,
					parsedCity,
					parsedArea,
					currentPageNumber
				);

				return {
					props: {
						pageName: "City Category Employer Pages",
						employeesCount: employeesData.count,
						employeesData: employeesData.users,
						currentPageNumber,
						totalPages,
						slug: employer[0],
						cityData: parsedCity,
						areaData: parsedArea,
						categoryData: null,
					},
				};
			} catch (error) {
				// noop
			}
		} else if (/^\w+$/.test(employer[0])) {
			// For old urls /employer/{citySlug} to redirect to new url with 301 status code
			const cityData = (CITY_LIST as unknown as JobCity[]).find((item) => {
				if (employer[0].includes("delhi")) {
					return item.slug.includes("delhi");
				}
				return employer[0] === item.slug;
			});

			if (cityData) {
				return {
					redirect: {
						destination: `/employer/job-posting-site-in-${employer[0]}`,
						statusCode: 301,
					},
				};
			}
		}
		return {
			redirect: {
				destination: "/employer",
				permanent: false,
			},
		};
	} catch (error) {
		return {
			notFound: true,
		};
	}
};

export default Employer;

const parseParams = (categorySlug, citySlug, areaSlug) => {
	let parsedCategory;
	let parsedCity;
	let parsedArea;

	if (categorySlug) {
		parsedCategory = (CATEGORY_DATA as unknown as JobCategory[]).find(
			(item) => categorySlug === item.slug || categorySlug === item?.old_slug
		);
	}

	if (citySlug) {
		parsedCity = (CITY_LIST as unknown as JobCity[]).find(
			(item) => item.slug === citySlug
		);
	}

	if (citySlug && areaSlug) {
		const cityAreas = (
			areaData as unknown as Record<
				string,
				{ area: Array<{ slug: string; id: string }> }
			>
		)[parsedCity.slug];
		parsedArea = cityAreas.area.find((a) => a.slug === areaSlug);
	}

	return { parsedCity, parsedArea, parsedCategory };
};

const getEmployeesDetails = async (
	parsedCategory: JobCategory,
	parsedCity: JobCity,
	parsedArea,
	page
) => {
	const filterParams: Record<string, any> = {
		page,
	};

	if (parsedCategory) {
		filterParams.categoryId = parsedCategory.id;
	}
	if (parsedCity) {
		filterParams.cityId = parsedCity.id;
	}
	if (parsedArea) {
		filterParams.areaId = parsedArea.id;
	}
	const employeesDataResp = await getGroupMembersData(filterParams);
	const employeesCount = employeesDataResp.data.data.count;

	const totalPages = Math.ceil(employeesCount / DEFAULT_NO_OF_ITEMS_PER_PAGE);
	// converting to snake_case
	const employeesData = employeesDataResp.data.data.users.map((element) =>
		Object.keys(element).reduce(
			(acc, k) => ({ ...acc, [_.snakeCase(k)]: element[k] }),
			{}
		)
	);
	employeesDataResp.data.data.users = employeesData;
	return { employeesData: employeesDataResp.data.data, totalPages };
};
